/**
 * Display helpers shared by ScoreRing and Companion: score ring colours, the ring
 * dash array and sport pill colours. The Today explorer card builders and filters
 * that lived here were removed with the Today pages (2 Oct 2026, forecast areas
 * programme); area pages and boards replace them.
 */


/**
 * Ring-gauge colour bands, first drawn for the retired Today pages and now used by
 * ScoreRing and Companion. Display-only presentation on the 0-100 display score;
 * it never feeds scoring, storage or band logic.
 */
export type TodayScoreMeta = { main: string; light: string; label: string };

export function todayScoreMeta(score: number | null): TodayScoreMeta {
  if (score === null) return { main: "#5C6472", light: "#B4BCC7", label: "No score" };
  if (score >= 85) return { main: "#0E9E7E", light: "#5FD3B6", label: "Prime" };
  if (score >= 70) return { main: "#C79A3A", light: "#E4C367", label: "Good" };
  if (score >= 55) return { main: "#C17B5D", light: "#DBA98F", label: "Fair" };
  return { main: "#5C6472", light: "#B4BCC7", label: "Quiet" };
}

const RING_CIRCUMFERENCE = 2 * Math.PI * 26;

/** SVG stroke-dasharray filling an r=26 ring to the score's share of 100. */
export function ringDashArray(score: number | null): string {
  const filled = (Math.max(0, Math.min(100, score ?? 0)) / 100) * RING_CIRCUMFERENCE;
  return `${filled.toFixed(2)} ${(RING_CIRCUMFERENCE - filled).toFixed(2)}`;
}

type SportPill = { bg: string; fg: string };

/** Pill colours keyed by app sportKey, grouped by family, with a neutral default. */
const SPORT_PILL: Record<string, SportPill> = {
  ski: { bg: "#E3EEF7", fg: "#2A5C86" },
  piste: { bg: "#E3EEF7", fg: "#2A5C86" },
  "off-piste": { bg: "#E3EEF7", fg: "#2A5C86" },
  touring: { bg: "#E7EAF2", fg: "#39466B" },
  mtb: { bg: "#F5E7DE", fg: "#96471F" },
  surf: { bg: "#E7EAF2", fg: "#39466B" },
  bodyboarding: { bg: "#E7EAF2", fg: "#39466B" },
  wind: { bg: "#DBF0F0", fg: "#0E6E76" },
  sailing: { bg: "#DBF0F0", fg: "#0E6E76" },
  "wing-foil": { bg: "#DBF0F0", fg: "#0E6E76" },
  "sailing-club": { bg: "#DBF0F0", fg: "#0E6E76" },
  "sailing-hp": { bg: "#DBF0F0", fg: "#0E6E76" },
  foil: { bg: "#DEEFF2", fg: "#0F7284" },
  "downwind-foil": { bg: "#DEEFF2", fg: "#0F7284" },
  diving: { bg: "#DEEFF2", fg: "#0F7284" },
  sup: { bg: "#E2F3EC", fg: "#0E6E56" },
  "sup-touring": { bg: "#E2F3EC", fg: "#0E6E56" },
  "sup-surf": { bg: "#E2F3EC", fg: "#0E6E56" },
  "sup-downwind": { bg: "#E2F3EC", fg: "#0E6E56" },
  swim: { bg: "#E2F3EC", fg: "#0E6E56" },
};

const DEFAULT_PILL: SportPill = { bg: "#EDEFEC", fg: "#4A5262" };

export function sportPill(sportKey: string): SportPill {
  return SPORT_PILL[sportKey] ?? DEFAULT_PILL;
}

